import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import React, { memo, useCallback, useState } from "react";

// US States list for filtering - copied exactly from index.tsx
const US_STATES = Object.keys(STATE_NAMES);

export interface StateFilterProps {
  /** Current committed values from omnisearch */
  committedStates: string[];
  /** Called when dropdown closes with new values */
  onCommit: (states: string[]) => void;
}

/**
 * Memoized State filter component that manages its own open state.
 * This prevents parent re-renders when the dropdown opens/closes.
 */
export const StateFilter = memo(function StateFilter({
  committedStates,
  onCommit,
}: StateFilterProps) {
  // === LOCAL STATE - changes here don't re-render parent ===
  const [open, setOpen] = useState(false);
  const [intermediateStates, setIntermediateStates] = useState<string[]>([]);

  // Display intermediate when open, committed when closed
  const selectedStates = open ? intermediateStates : committedStates;

  // Check if at default (all states selected)
  const isAtDefault = selectedStates.length === US_STATES.length;

  // Initialize intermediate state when dropdown opens
  const handleOpen = useCallback(() => {
    setIntermediateStates([...committedStates]);
    setOpen(true);
  }, [committedStates]);

  // Commit changes when dropdown closes
  const handleClose = useCallback(() => {
    setOpen(false);
    onCommit(intermediateStates);
  }, [intermediateStates, onCommit]);

  // Handle selection changes - only updates intermediate state
  const handleChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as string[];
    // MUI Select fires onChange when clicking buttons inside - ignore if null/undefined values present
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    const values = raw.filter(
      (v) => v !== "select-all" && v !== null && v !== undefined && US_STATES.includes(v)
    );
    setIntermediateStates(values);
  }, []);

  // Toggle all states handler - toggles between all selected and none selected
  const handleToggleAll = useCallback(() => {
    if (intermediateStates.length === US_STATES.length) {
      setIntermediateStates([]);
    } else {
      setIntermediateStates([...US_STATES]);
    }
  }, [intermediateStates.length]);

  // Reset states to default (all states selected)
  const handleReset = useCallback(() => {
    setIntermediateStates([...US_STATES]);
  }, []);

  return (
    <FormControl variant="standard" size="small" sx={{ width: 140 }}>
      <InputLabel id="state-filter-label">State/Territory</InputLabel>
      <Select
        labelId="state-filter-label"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple
        value={selectedStates}
        onChange={handleChange}
        label="State/Territory"
        variant="standard"
        inputProps={{
          autoComplete: "off",
          "data-lpignore": "true",
          "data-form-type": "other",
          "data-1p-ignore": "true",
        }}
        sx={{
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return "None";
          }
          if (selected.length === US_STATES.length) {
            return `All (${selected.length})`;
          }
          if (selected.length <= 2) {
            return (selected as string[])
              .map((s) => {
                const name = STATE_NAMES[s as keyof typeof STATE_NAMES];
                return name ? `${name} (${s})` : s;
              })
              .join(", ");
          }
          return `${selected.length} selected`;
        }}
        MenuProps={{
          // Prevent menu from affecting body scroll, which triggers DataGrid's ResizeObserver
          disableScrollLock: true,
          MenuListProps: {
            autoFocusItem: selectedStates.length === US_STATES.length ? false : true,
          },
          PaperProps: {
            sx: { minWidth: 280 },
          },
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedStates.length === US_STATES.length}
                indeterminate={
                  selectedStates.length > 0 && selectedStates.length < US_STATES.length
                }
                onChange={handleToggleAll}
              />
            }
            label="Show/Hide All"
            sx={{ margin: 0 }}
          />
          {!isAtDefault && <Button onClick={handleReset}>Reset</Button>}
        </Box>
        {US_STATES.map((state) => (
          <MenuItem key={state} value={state}>
            <Checkbox checked={selectedStates.includes(state)} />
            {STATE_NAMES[state as keyof typeof STATE_NAMES]
              ? `${STATE_NAMES[state as keyof typeof STATE_NAMES]} (${state})`
              : state}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});
